// EXTERNAL DEPENDENCIES
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'

// HELPERS
import { usePrefixedContent } from '@/utils/content-helpers'
import { BLUE_SELLER_PROFILE_PICTOGRAM_URL } from '../../../images/pictograms'

// TEST IDS
import { SHARE_PROFILE_SHEET_CONTAINER_TEST_ID } from '@/constants/test-ids'

// COMPONENTS
import {
  Button,
  Col,
  HeadingText,
  Row,
  Sheet,
  IconButton,
  ArrowLeftIcon,
  CopyToClipboardIcon,
  BodyText,
  CaptionText,
  Toast,
  Link,
} from '@paypalcorp/pp-react'

// STYLES
import styles from './styles.module.less'

// TYPES
type ErrorMessageTypes = 'error' | 'success'

/**
 * Displays the sheet on clicking Share Profile Button
 */
const ShareProfileSheetContainer = ({
  isShareSheetOpen,
  setIsShareSheetOpen,
  isBuyer,
}: {
  isShareSheetOpen?: boolean
  setIsShareSheetOpen?: (isOpen: boolean) => void
  isBuyer?: boolean
}) => {
  const getContent = usePrefixedContent('components.ui.share-profile-sheet.')
  const sheetContentHeadingText = getContent('sheetContentHeadingText')
  const sheetCopyText = getContent('sheetCopyText')
  const sheetLinkPasteText = getContent('sheetLinkPasteText')
  const copyLinkLabel = getContent('copyLinkLabel')
  const doneButtonLabel = getContent('doneButtonLabel')
  const successToastMessage = getContent('toastMessage')
  const errorToastMessage = getContent('errorToastMessage')
  const sheetTitle = getContent('sheetTitle')
  const closeButtonLabel = getContent('closeButtonLabel')
  const backButtonLabel = getContent('backButtonLabel')

  const { handle } = useParams()
  const shareProfileURL = `https://www.paypal.biz/${handle}`

  const sheetContentContainerRef = useRef(null)
  const [toastMessageType, setToastMessageType] =
    useState<ErrorMessageTypes>(null)

  const handleCloseSheet = () => {
    setIsShareSheetOpen(false)
    setToastMessageType(null)
  }

  const handleSheetCloseButtonClick = () => {
    handleCloseSheet()
    return false
  }

  const handleToastClose = () => {
    setToastMessageType(null)
  }

  const unsecuredCopyToClipboard = useCallback(() => {
    let newToastMessageType: ErrorMessageTypes = 'success'
    const el = document.createElement('input')
    el.value = shareProfileURL
    sheetContentContainerRef.current.appendChild(el)
    el.select()
    el.setSelectionRange(0, 99999) // For mobile devices

    try {
      document.execCommand('copy')
    } catch (_e) {
      newToastMessageType = 'error'
    }

    setToastMessageType(newToastMessageType)

    sheetContentContainerRef.current.removeChild(el)
  }, [shareProfileURL])

  /**
   * The use of navigator.clipboard requires a secure origin. Clipboard API is only supported for pages served over HTTPS.
   * Check if using HTTPS and navigator.clipboard is available
   * Then uses standard clipboard API, otherwise uses fallback method
   */
  const handleCopyLinkClick = useCallback(async () => {
    if (window.isSecureContext && navigator.clipboard) {
      let newToastMessageType: ErrorMessageTypes = 'success'

      try {
        await navigator.clipboard.writeText(shareProfileURL)
      } catch (_e) {
        newToastMessageType = 'error'
      }

      setToastMessageType(newToastMessageType)
    } else {
      unsecuredCopyToClipboard() // legacy function as a backup
    }
  }, [shareProfileURL, unsecuredCopyToClipboard])

  const copyLinkContainerGrid = () => (
    <Row className={styles.sheetCopyLinkContainer}>
      <Col className={styles.sheetCopyIcon} sm={2}>
        <CopyToClipboardIcon
          aria-hidden
          size='sm'
          color='sysColorAccent1Main'
        />
      </Col>
      <Col className={styles.sheetCopyTextContainer}>
        <BodyText>{sheetCopyText}</BodyText>
        <CaptionText className={styles.sheetLinkPasteText}>
          {sheetLinkPasteText}
        </CaptionText>
      </Col>
      <Col className={styles.sheetCopyLink} sm={3}>
        <Link
          name='copied_link_toast'
          onClick={handleCopyLinkClick}
          tabIndex={0}
        >
          {copyLinkLabel}
        </Link>
      </Col>
    </Row>
  )

  useEffect(() => {
    if (isBuyer && isShareSheetOpen) {
      handleCopyLinkClick()
      setIsShareSheetOpen(false)
    }
  }, [isBuyer, isShareSheetOpen, setIsShareSheetOpen, handleCopyLinkClick])

  const hiddenContainerToHandleUnsecuredBuyerShare = (
    <div ref={sheetContentContainerRef}>
      <div aria-hidden style={{ visibility: 'hidden' }} />
    </div>
  )

  return (
    <>
      {isBuyer ? (
        hiddenContainerToHandleUnsecuredBuyerShare
      ) : (
        <Sheet
          data-testid={SHARE_PROFILE_SHEET_CONTAINER_TEST_ID}
          contentContainerRef={sheetContentContainerRef}
          isOpen={isShareSheetOpen}
          onBeforeClose={handleSheetCloseButtonClick}
          title={sheetTitle}
          hideTitle={true}
          onCloseComplete={handleCloseSheet}
          closeButtonLabel={closeButtonLabel}
          action={
            <IconButton
              svgIconComponent={ArrowLeftIcon}
              onClick={handleCloseSheet}
              buttonType='tertiary'
            >
              {backButtonLabel}
            </IconButton>
          }
        >
          <img
            src={BLUE_SELLER_PROFILE_PICTOGRAM_URL}
            alt=''
            className={styles.sheetPictogramImage}
          />
          <HeadingText size='lg' className={styles.sheetContentHeading}>
            {sheetContentHeadingText}
          </HeadingText>
          {copyLinkContainerGrid()}
          <Button
            variant='primary'
            className={styles.sheetDoneButton}
            onClick={handleCloseSheet}
          >
            {doneButtonLabel}
          </Button>
        </Sheet>
      )}
      {toastMessageType && (
        <Toast
          id='copied_link_toast'
          message={
            toastMessageType === 'success'
              ? successToastMessage
              : errorToastMessage
          }
          isSuccess={toastMessageType === 'success'}
          onClose={handleToastClose}
        />
      )}
    </>
  )
}

export default ShareProfileSheetContainer
